var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { FaceValidator } from './FaceValidator';
import { ValidationStatus } from './types';
import { getMessage } from './i18n';
const DEFAULT_VIDEO_WIDTH = 512;
const DEFAULT_VIDEO_HEIGHT = 384;
const DEFAULT_LOCALE = 'pt-BR';
const MESSAGE_DELAY_MS = 1500; // Delay entre mudanças de mensagem (1.5 segundos)
// Status que devem ser exibidos imediatamente sem delay
const CRITICAL_STATUSES = [
    ValidationStatus.SUCCESS,
    ValidationStatus.ERROR,
    ValidationStatus.CAPTURING,
    ValidationStatus.INITIALIZING,
];
const UI_LABELS = {
    'pt-BR': {
        previewQuestion: 'O que você achou?',
        savePhoto: 'Salvar foto',
        tryAgain: 'Tentar novamente',
        cancel: 'Cancelar',
    },
    en: {
        previewQuestion: 'What do you think?',
        savePhoto: 'Save photo',
        tryAgain: 'Try again',
        cancel: 'Cancel',
    },
    es: {
        previewQuestion: '¿Qué te pareció?',
        savePhoto: 'Guardar foto',
        tryAgain: 'Intentar de nuevo',
        cancel: 'Cancelar',
    },
};
const resolveLocale = (locale) => {
    if (!locale)
        return DEFAULT_LOCALE;
    const normalized = locale.toLowerCase();
    if (normalized.startsWith('en'))
        return 'en';
    if (normalized.startsWith('es'))
        return 'es';
    return 'pt-BR';
};
export const ReactSelfieCapture = ({ onCapture, onDismiss, locale, videoWidth = DEFAULT_VIDEO_WIDTH, videoHeight = DEFAULT_VIDEO_HEIGHT, debugMode = false, modelPath, styles, labels, }) => {
    const effectiveLocale = resolveLocale(locale);
    const baseLabels = UI_LABELS[effectiveLocale];
    const ui = Object.assign(Object.assign({}, baseLabels), (labels !== null && labels !== void 0 ? labels : {}));
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const sdkRef = useRef(null);
    const messageTimeoutRef = useRef(null);
    const lastDisplayTimeRef = useRef(0);
    const latestMessageRef = useRef('');
    const [status, setStatus] = useState(ValidationStatus.INITIALIZING);
    const [message, setMessage] = useState(getMessage(ValidationStatus.INITIALIZING, effectiveLocale));
    const [displayedMessage, setDisplayedMessage] = useState(getMessage(ValidationStatus.INITIALIZING, effectiveLocale));
    const [showCapture, setShowCapture] = useState(true);
    const [photoCaptured, setPhotoCaptured] = useState(null);
    const handleStatusUpdate = useCallback((newStatus, newMessage) => {
        setStatus(newStatus);
        setMessage(newMessage);
    }, []);
    // Gerenciar delay entre mudanças de mensagem para melhorar UX
    useEffect(() => {
        // Sempre atualizar o ref com a mensagem mais recente
        latestMessageRef.current = message;
        // Se a mensagem é a mesma que já está sendo exibida, não fazer nada
        if (message === displayedMessage) {
            return;
        }
        // Se é um status crítico, exibir imediatamente
        if (CRITICAL_STATUSES.includes(status)) {
            // Cancelar e limpar qualquer timeout pendente
            if (messageTimeoutRef.current) {
                clearTimeout(messageTimeoutRef.current);
                messageTimeoutRef.current = null;
            }
            setDisplayedMessage(message);
            lastDisplayTimeRef.current = Date.now();
            return;
        }
        // Para status não-críticos:
        const now = Date.now();
        const timeSinceLastDisplay = now - lastDisplayTimeRef.current;
        // Se já passou tempo suficiente, atualizar imediatamente
        if (timeSinceLastDisplay >= MESSAGE_DELAY_MS) {
            setDisplayedMessage(message);
            lastDisplayTimeRef.current = now;
            return;
        }
        // Se já existe um timeout agendado, não criar outro
        // (deixar o timeout completar e exibir a mensagem mais recente do ref)
        if (messageTimeoutRef.current) {
            return;
        }
        // Agendar atualização para quando completar o delay mínimo
        const remainingTime = MESSAGE_DELAY_MS - timeSinceLastDisplay;
        messageTimeoutRef.current = setTimeout(() => {
            // Usar a mensagem mais recente do ref
            setDisplayedMessage(latestMessageRef.current);
            lastDisplayTimeRef.current = Date.now();
            messageTimeoutRef.current = null;
        }, remainingTime);
    }, [message, status, displayedMessage]);
    function blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
    const handleCaptureSuccess = useCallback((imageBlob) => {
        setStatus(ValidationStatus.SUCCESS);
        setMessage(getMessage(ValidationStatus.SUCCESS, effectiveLocale));
        blobToBase64(imageBlob).then((base64) => {
            setPhotoCaptured(base64);
            setShowCapture(false);
        });
    }, [effectiveLocale]);
    const saveImage = useCallback(() => {
        onCapture(photoCaptured !== null && photoCaptured !== void 0 ? photoCaptured : null);
        onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss();
    }, [onCapture, onDismiss, photoCaptured]);
    const captureAgain = useCallback(() => {
        setPhotoCaptured(null);
        setStatus(ValidationStatus.INITIALIZING);
        setMessage(getMessage(ValidationStatus.INITIALIZING, effectiveLocale));
        setShowCapture(true);
    }, [effectiveLocale]);
    const handleError = useCallback((_errorType, error) => {
        setMessage(error.message);
        setStatus(ValidationStatus.ERROR);
    }, []);
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!showCapture) {
            if (sdkRef.current) {
                sdkRef.current.destroy();
                sdkRef.current = null;
            }
            return;
        }
        if (!videoElement)
            return;
        let isCancelled = false;
        const setupCameraAndSdk = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            try {
                const canvasElement = canvasRef.current;
                if (canvasElement && !canvasElement.width) {
                    canvasElement.width = videoWidth;
                    canvasElement.height = videoHeight;
                }
                if (sdkRef.current || isCancelled)
                    return;
                const options = {
                    videoElement,
                    overlayCanvasElement: (_a = canvasRef.current) !== null && _a !== void 0 ? _a : undefined,
                    locale: effectiveLocale,
                    debugMode,
                    onStatusUpdate: handleStatusUpdate,
                    onCaptureSuccess: handleCaptureSuccess,
                    onError: handleError,
                    videoWidth,
                    videoHeight,
                    ui: 'none',
                    autoStart: true,
                    mirror: true,
                };
                if (modelPath) {
                    options.modelPath = modelPath;
                }
                const instance = new FaceValidator(options);
                sdkRef.current = instance;
            }
            catch (error) {
                if (isCancelled)
                    return;
                const err = error;
                setMessage(err.message);
                setStatus(ValidationStatus.ERROR);
            }
        });
        setupCameraAndSdk().catch(() => { });
        return () => {
            isCancelled = true;
            if (sdkRef.current) {
                sdkRef.current.destroy();
                sdkRef.current = null;
            }
        };
    }, [
        showCapture,
        effectiveLocale,
        videoWidth,
        videoHeight,
        debugMode,
        modelPath,
        handleStatusUpdate,
        handleCaptureSuccess,
        handleError,
    ]);
    const handleCloseClick = () => {
        onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss();
        onCapture(null);
        setShowCapture(false);
    };
    const isLoading = () => status === ValidationStatus.INITIALIZING;
    const isPreview = !showCapture && Boolean(photoCaptured);
    const isCamera = showCapture;
    const containerStyle = Object.assign({ width: '100%', maxWidth: 640, boxSizing: 'border-box' }, styles === null || styles === void 0 ? void 0 : styles.container);
    const bannerBase = Object.assign({ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, padding: '10px 12px', marginBottom: 10, borderRadius: 10, fontWeight: 600, marginTop: 30, height: 52, boxSizing: 'border-box', transition: 'all 0.4s ease-in-out' }, styles === null || styles === void 0 ? void 0 : styles.messageBanner);
    const bannerStyle = Object.assign(Object.assign({}, bannerBase), { backgroundColor: '#fff7df', color: '#b67219' });
    if (isCamera) {
        if (status === ValidationStatus.SUCCESS) {
            bannerStyle.backgroundColor = '#e0ffdf';
            bannerStyle.color = '#26c026';
        }
        else if (status === ValidationStatus.ERROR) {
            bannerStyle.backgroundColor = '#ffdfdf';
            bannerStyle.color = '#c02626';
        }
    }
    const mediaWrapper = Object.assign({ width: '100%', maxWidth: 512, height: 384, margin: '0 auto', borderRadius: 10, overflow: 'hidden', position: 'relative', backgroundColor: '#1a1a1a' }, styles === null || styles === void 0 ? void 0 : styles.media);
    const buttonBase = {
        display: 'block',
        width: '100%',
        padding: '8px 12px',
        borderRadius: 4,
        border: '1px solid #d9d9d9',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        fontSize: 14,
    };
    const primaryButtonStyle = Object.assign(Object.assign(Object.assign({}, buttonBase), { backgroundColor: '#1dbe32', color: '#ffffff', borderColor: '#1dbe32' }), styles === null || styles === void 0 ? void 0 : styles.primaryButton);
    const secondaryButtonStyle = Object.assign(Object.assign({}, buttonBase), styles === null || styles === void 0 ? void 0 : styles.secondaryButton);
    const actionsAreaStyle = {
        marginTop: 10,
    };
    return (_jsxs("div", { style: containerStyle, children: [_jsx("div", { style: bannerStyle, children: isPreview ? ui.previewQuestion : displayedMessage }), _jsx("div", { style: mediaWrapper, children: isCamera ? (_jsxs(_Fragment, { children: [_jsx("video", { ref: videoRef, autoPlay: true, playsInline: true, muted: true, style: { width: '100%', height: '100%', objectFit: 'cover' } }), _jsx("canvas", { ref: canvasRef, style: {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'none',
                                transform: 'scaleX(-1)',
                            } })] })) : isPreview && photoCaptured ? (_jsx("img", { src: photoCaptured, alt: "Selfie preview", style: {
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: 'scaleX(-1)',
                        backgroundColor: 'transparent',
                        border: 'none',
                    } })) : (_jsx("div", { "aria-hidden": true })) }), _jsx("div", { style: actionsAreaStyle, children: isPreview ? (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: captureAgain, style: secondaryButtonStyle, children: ui.tryAgain }), _jsx("div", { style: { height: 8 } }), _jsx("button", { type: "button", onClick: saveImage, disabled: !photoCaptured, style: Object.assign(Object.assign({}, primaryButtonStyle), { opacity: photoCaptured ? 1 : 0.6, cursor: photoCaptured ? 'pointer' : 'not-allowed' }), children: ui.savePhoto })] })) : (_jsx("button", { type: "button", onClick: handleCloseClick, style: secondaryButtonStyle, children: ui.cancel })) })] }));
};
